import { useQuery } from "@tanstack/react-query";
import { logMailsApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";
import { DataI, ServiceResponseI } from "../../interfaces";
import { LogDataI, PropsLogDataI } from "../../interfaces";

type GetLogMailsArgs = PropsLogDataI & {
  /** "" para la vista admin, "/sri" para la vista de comprobantes SRI. */
  path?: string;
};

const getLogMails = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  success,
  documentType,
  path = "",
}: GetLogMailsArgs): Promise<ServiceResponseI<DataI<LogDataI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (success !== undefined) params.append("success", success);
    if (documentType) params.append("documentType", documentType);

    const { data } = await logMailsApi.get<ServiceResponseI<DataI<LogDataI>>>(
      path,
      {
        params,
        headers: { Authorization: validateToken },
      },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useLogMails = ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  success,
  documentType,
}: PropsLogDataI) => {
  const logMailsQuery = useQuery(
    [
      "log-mails",
      {
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        success,
        documentType,
      },
    ],
    () =>
      getLogMails({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        success,
        documentType,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { logMailsQuery };
};

/**
 * Log de correos acotado a comprobantes SRI (facturas / retenciones / notas).
 * Pega a GET /api/log-mails/sri — el backend ya lo filtra al clientId del
 * usuario cuando no es superadmin, así que sirve para la sección SRI del cliente.
 */
export const useSriMailLog = ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  success,
  documentType,
}: PropsLogDataI) => {
  const sriMailLogQuery = useQuery(
    [
      "sri-mail-log",
      {
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        success,
        documentType,
      },
    ],
    () =>
      getLogMails({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        success,
        documentType,
        path: "/sri",
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { sriMailLogQuery };
};
